import m, {type ComponentType, MithrilComponent, state} from '@bitstillery/mithril'
import type {CmsAllPlaceholdersResourcesResponse} from '@bitstillery/common/api/fact2server'
import {$s} from '@/app'
import {Spinner} from '@bitstillery/common/components/portal'

import {ContentFrame} from '@/cms/components/shell/content_frame/content_frame'
import {import_compiled_cms_module, is_shop_layout, resolve_cms_component} from '@/cms/runtime/page'
import {merge_deep} from '@bitstillery/common/lib/utils'
import {unchecked} from '@bitstillery/common/lib/unchecked'
import {flat_keys_to_nested} from '@bitstillery/common/lib/portal_cms_i18n'
import {api, i18next, logger} from '@bitstillery/common/app'

/**
 * Whether `origin` is the Discover CMS editor allowed to embed this preview and post compiled JS.
 * The payload feeds a code-execution sink (`import_compiled_cms_module`), so this must be an exact
 * full-origin match (scheme + host + port) — never a `hostname.startsWith` check, which would let
 * e.g. `discover.evil.com` through. The editor runs at the same Discover host for both themes.
 */
export function is_allowed_discover_origin(origin: string): boolean {
    switch (process.env.BUN_ENV) {
        case 'production':
            return origin === 'https://discover.movingspirits.nl'
        case 'staging':
            return origin === 'https://discover.staging.v5.springtimegroup.nl'
        case 'development':
        default:
            return origin === 'http://discover.localhost'
    }
}

/** Must match Discover `CmsViewPreview` postMessage `type`. */
export const CMS_DISCOVERY_PREVIEW_MESSAGE = 'cms-discovery-preview'

/** Discover listens for this after the iframe loads. */
export const CMS_PREVIEW_READY_MESSAGE = 'cms-preview-ready'

/** What Discover's CMS editor posts: `portal/cms/view/preview_page.tsx` and `preview_fragment.tsx`. */
interface CmsPreviewMessage {
    compiled_js: string
    hero_mode?: string | null
    /** Translations the editor created or edited this session, `{language: {flat_key: text}}`. */
    i18n?: Record<string, Record<string, string>>
    path: string
    type: string
}

/**
 * Embedded at `/cms/preview` for Discover’s CMS editor iframe. Accepts `{ compiled_js, view }` via
 * postMessage from a trusted Discover origin (same shell as production portal).
 */
export class CmsDiscoverPreviewFrame extends MithrilComponent {
    // @i18n include: breadcrumbs.cms
    // @i18n include: breadcrumbs.preview
    component: ComponentType | undefined
    page = state({
        load_error: '',
    })

    /**
     * The message being passed by Discover's CMS editor (preview_page.tsx).
     * @param message
     * @returns
     */
    private preview_controller = async ({data: message, origin, source}: MessageEvent<unknown>) => {
        if (source !== window.parent) {
            return
        }
        if (!is_allowed_discover_origin(origin)) {
            return
        }
        // Only the trusted editor gets this far.
        const data = unchecked<CmsPreviewMessage>(message)
        logger.info(`origin host accepted: ${origin}`)

        if (data.type !== CMS_DISCOVERY_PREVIEW_MESSAGE) {
            return
        }

        // Re-fetch the page's full i18n set on every preview update so placeholders the editor just created
        // (or edited) render without a hard reload. `page_only: false` returns globals + this page's bound
        // keys — the same union a real page load assembles — so newly added GLOBAL placeholders also appear,
        // not just page-bound ones. Copy is secondary to the layout being previewed: a failed fetch must
        // degrade to stale translations, never abort the handler and leave the editor staring at a blank frame.
        let resources: CmsAllPlaceholdersResourcesResponse['resources'] | undefined
        try {
            const {result, success} = await api.get(`portal/cms/i18n`, {
                language_code: $s.language,
                page_only: false,
                path: data.path,
            })
            if (success) resources = result.resources
        } catch (err) {
            logger.warn(`preview i18n fetch failed, rendering with the translations already loaded: ${String(err)}`)
        }

        merge_deep($s.page, {compiled_js: data.compiled_js, error: null, hero_mode: data.hero_mode ?? 'full', path: data.path})
        this.page.load_error = ''
        this.component = undefined

        const translation = resources?.[$s.language]?.translation

        if (translation) {
            console.log('ADD BUNDLE', translation)
            i18next.addResourceBundle($s.language, 'translation', translation, true, true)
        }

        // Overlay translations the editor created/edited this session (sent over postMessage). These may not
        // be committed yet, so the DB fetch above can't see them; applying them here makes newly created
        // placeholders render immediately. Overwrites the fetched values for any key the editor just changed.
        const live_i18n = data.i18n?.[$s.language]
        if (live_i18n && Object.keys(live_i18n).length > 0) {
            logger.info(`updating page i18n bundle with ${Object.keys(live_i18n).length} keys`)
            i18next.addResourceBundle($s.language, 'translation', flat_keys_to_nested(live_i18n), true, true)
        }

        try {
            const mod = await import_compiled_cms_module(data.compiled_js)
            this.component = resolve_cms_component(mod)
            if (!this.component) {
                this.page.load_error =
                    'CMS module has no default or component export (e.g. add export default or name it CmsView)'
            }
        } catch (err) {
            this.page.load_error = err instanceof Error ? err.message : String(err)
        }
        m.redraw()
    }

    private on_message = (event: MessageEvent) => {
        void this.preview_controller(event)
    }

    override oninit() {
        window.addEventListener('message', this.on_message)
        if (window.parent !== window) {
            window.parent.postMessage({type: CMS_PREVIEW_READY_MESSAGE}, '*')
        }
    }

    override onremove() {
        window.removeEventListener('message', this.on_message)
    }

    view() {
        const inner = () => {
            if (this.page.load_error) {
                return <main class='cms-dynamic-page cms-dynamic-page--error page-content'>{this.page.load_error}</main>
            }

            if (!this.component) {
                return (
                    <main class='cms-dynamic-page cms-dynamic-page--loading page-content'>
                        <Spinner />
                    </main>
                )
            }
            return <main class='cms-dynamic-page page-content'>{m(this.component)}</main>
        }

        if (is_shop_layout()) {
            return <div class='cms-dynamic-page-root cms-dynamic-page-root--portal'>{inner()}</div>
        }

        if (!this.component) {
            return null
        }

        return (
            <ContentFrame>
                <div class='content'>{inner()}</div>
            </ContentFrame>
        )
    }
}
